import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { 
  MapPin, Truck, Package, Eye, Phone, Clock, 
  CheckCircle, XCircle, RefreshCw, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { loadMapbox } from '../utils/mapboxLoader';
import { fitToLocations, toLngLat, upsertMarker } from '../utils/mapboxMap';

import { API_URL, WS_URL } from '../config/api';

const API = API_URL;

const formatPrice = (price) => new Intl.NumberFormat('fr-FR').format(price);

const ORDER_STATUSES = {
  pending: { label: 'En attente', color: 'amber' },
  assigned: { label: 'Assignée', color: 'blue' },
  picked_up: { label: 'Récupérée', color: 'indigo' },
  in_transit: { label: 'En route', color: 'purple' },
  delivered: { label: 'Livrée', color: 'green' },
  cancelled: { label: 'Annulée', color: 'red' }
};

const AdminLiveTracking = ({ token }) => {
  const [drivers, setDrivers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderStats, setOrderStats] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapLoaded, setMapLoaded] = useState(false);
  
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const mapboxRef = useRef(null);
  const markersRef = useRef({});
  const wsRef = useRef(null);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [driversRes, ordersRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/drivers/locations`, { headers }),
        axios.get(`${API}/admin/orders?limit=50`, { headers }),
        axios.get(`${API}/admin/orders/stats`, { headers })
      ]);
      
      setDrivers(driversRes.data.drivers || []);
      setOrders(ordersRes.data.orders || []);
      setOrderStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();

    loadMapbox()
      .then((mapboxgl) => {
        mapboxRef.current = mapboxgl;
        setMapLoaded(true);
      })
      .catch(() => toast.error('Erreur chargement Mapbox'));
  }, [fetchData]);

  // Initialize map (only once)
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || mapInstance.current) return;

    const center = { latitude: 5.3599, longitude: -4.0083 };

    mapInstance.current = new mapboxRef.current.Map({
      container: mapRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: toLngLat(center),
      zoom: 12,
    });
    mapInstance.current.addControl(new mapboxRef.current.NavigationControl(), 'top-right');
  }, [mapLoaded]);

  // Update markers when drivers change (without reinitializing map)
  useEffect(() => {
    if (mapInstance.current) {
      updateDriverMarkers();
    }
  }, [drivers]);

  const updateDriverMarkers = () => {
    if (!mapInstance.current || !mapboxRef.current) return;

    const currentDriverIds = new Set(drivers.map(d => d.driver_id));

    // Remove markers for drivers that are no longer in the list
    Object.keys(markersRef.current).forEach(driverId => {
      if (!currentDriverIds.has(driverId)) {
        markersRef.current[driverId].remove();
        delete markersRef.current[driverId];
      }
    });

    // Update or add markers for current drivers
    drivers.forEach(driver => {
      if (!driver.location?.latitude) return;

      const pos = { latitude: driver.location.latitude, longitude: driver.location.longitude };
      const color = driver.status === 'available' ? '#22c55e' : driver.status === 'busy' ? '#eab308' : '#2563eb';

      if (markersRef.current[driver.driver_id]) {
        // Update existing marker position
        markersRef.current[driver.driver_id].setLngLat(toLngLat(pos));
      } else {
        // Create new marker
        const markerRef = { current: null };
        const marker = upsertMarker(mapboxRef.current, mapInstance.current, markerRef, pos, {
          color,
          title: driver.driver_name
        });

        const popup = new mapboxRef.current.Popup({ offset: 24 }).setHTML(`
          <div style="padding: 8px; min-width: 150px;">
            <strong>${driver.driver_name}</strong><br/>
            <span style="color: #666;">${driver.vehicle_type}</span><br/>
            <span style="color: ${driver.status === 'available' ? 'green' : 'orange'};">
              ${driver.status === 'available' ? '✅ Disponible' : '🔴 Occupé'}
            </span>
          </div>
        `);

        marker.getElement().addEventListener('click', () => {
          marker.setPopup(popup).togglePopup();
          setSelectedDriver(driver);
        });

        markersRef.current[driver.driver_id] = marker;
      }
    });

    // Only fit bounds if this is the first time we have markers
    if (Object.keys(markersRef.current).length === drivers.length && drivers.length > 0) {
      fitToLocations(mapboxRef.current, mapInstance.current, drivers.map(d => d.location).filter(Boolean), 50);
    }
  };

  // WebSocket for real-time updates - URL CORRIGÉE
  useEffect(() => {
    const connectWebSocket = () => {
      if (!token) return;
      const wsUrl = `${WS_URL}/api/ws/orders/admin_tracking?token=${encodeURIComponent(token)}`;
      console.log('WebSocket connecting to:', wsUrl);
      
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('Admin tracking WebSocket connected');
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'driver_location') {
            // Update driver location
            setDrivers(prev => prev.map(d =>
              d.driver_id === data.driver_id
                ? { ...d, location: data.location }
                : d
            ));

            // Update marker on map smoothly
            if (markersRef.current[data.driver_id] && mapInstance.current) {
              const currentLngLat = markersRef.current[data.driver_id].getLngLat();
              const newLngLat = toLngLat(data.location);

              // Smooth animation for marker movement
              markersRef.current[data.driver_id].setLngLat(newLngLat);

              // Optionally pan to driver if selected
              if (selectedDriver?.driver_id === data.driver_id) {
                mapInstance.current.easeTo({
                  center: newLngLat,
                  zoom: 16,
                  duration: 1000,
                  easing: (t) => t * (2 - t),
                });
              }
            }
          }
          
          if (data.type === 'order_update') {
            // Refresh orders
            fetchData();
          }
          
          if (data.type === 'new_order') {
            toast.info('Nouvelle commande !', {
              description: `${data.order?.delivery_address?.city || 'Nouvelle commande'}`
            });
            fetchData();
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };
      
      ws.onclose = (event) => {
        console.log('WebSocket closed, reconnecting in 3s...', event.code);
        setTimeout(connectWebSocket, 3000);
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
      
      wsRef.current = ws;
    };
    
    connectWebSocket();
    
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
    
    return () => {
      clearInterval(pingInterval);
      wsRef.current?.close();
    };
  }, [fetchData, token]);

  // Focus on driver
  const focusOnDriver = (driver) => {
    if (!mapInstance.current || !driver.location) return;
    
    mapInstance.current.easeTo({ center: toLngLat(driver.location), zoom: 16 });
    setSelectedDriver(driver);
  };

  // Get active orders count by status
  const getOrderCountByStatus = (status) => {
    return orders.filter(o => o.status === status).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Order Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="bg-slate-700/50 rounded-xl p-4">
          <p className="text-2xl font-bold text-white">{orderStats?.total_orders || 0}</p>
          <p className="text-xs text-slate-400">Total commandes</p>
        </div>
        <div className="bg-amber-500/20 rounded-xl p-4">
          <p className="text-2xl font-bold text-amber-400">{orderStats?.pending_orders || 0}</p>
          <p className="text-xs text-slate-400">En attente</p>
        </div>
        <div className="bg-blue-500/20 rounded-xl p-4">
          <p className="text-2xl font-bold text-blue-400">{getOrderCountByStatus('assigned')}</p>
          <p className="text-xs text-slate-400">Assignées</p>
        </div>
        <div className="bg-purple-500/20 rounded-xl p-4">
          <p className="text-2xl font-bold text-purple-400">{getOrderCountByStatus('in_transit')}</p>
          <p className="text-xs text-slate-400">En cours</p>
        </div>
        <div className="bg-green-500/20 rounded-xl p-4">
          <p className="text-2xl font-bold text-green-400">{orderStats?.completed_orders || 0}</p>
          <p className="text-xs text-slate-400">Livrées</p>
        </div>
        <div className="bg-emerald-500/20 rounded-xl p-4">
          <p className="text-2xl font-bold text-emerald-400">{formatPrice(orderStats?.total_revenue_fcfa || 0)}</p>
          <p className="text-xs text-slate-400">Revenus FCFA</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Map */}
        <div className="lg:col-span-2 bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <div className="p-4 border-b border-slate-700 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-cyan-400" />
              <h3 className="font-bold">Suivi en temps réel</h3>
            </div>
            <Button size="sm" variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
            </Button>
          </div>
          
          <div ref={mapRef} className="w-full h-96" data-testid="admin-tracking-map" />
          
          {/* Legend */}
          <div className="p-3 bg-slate-700/50 flex items-center gap-6 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full" />
              <span className="text-slate-300">Disponible</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-yellow-500 rounded-full" />
              <span className="text-slate-300">Occupé</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full" />
              <span className="text-slate-300">Hors ligne</span>
            </div>
          </div>
        </div>

        {/* Active Drivers List */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <div className="p-4 border-b border-slate-700">
            <h3 className="font-bold flex items-center gap-2">
              <Truck className="w-5 h-5 text-blue-400" />
              Livreurs actifs ({drivers.length})
            </h3>
          </div>
          
          <div className="max-h-96 overflow-y-auto">
            {drivers.length > 0 ? (
              <div className="divide-y divide-slate-700">
                {drivers.map((driver) => (
                  <div 
                    key={driver.driver_id}
                    className={`p-3 hover:bg-slate-700/30 cursor-pointer transition-colors ${
                      selectedDriver?.driver_id === driver.driver_id ? 'bg-slate-700/50' : ''
                    }`}
                    onClick={() => focusOnDriver(driver)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${
                          driver.status === 'available' ? 'bg-green-400' :
                          driver.status === 'busy' ? 'bg-yellow-400' : 'bg-slate-400'
                        }`} />
                        <div>
                          <p className="font-medium text-white text-sm">{driver.driver_name}</p>
                          <p className="text-xs text-slate-400">{driver.vehicle_type}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {driver.phone && (
                          <a 
                            href={`tel:${driver.phone}`}
                            className="p-1.5 bg-green-500/20 rounded text-green-400 hover:bg-green-500/30"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Phone className="w-3 h-3" />
                          </a>
                        )}
                        <button 
                          className="p-1.5 bg-blue-500/20 rounded text-blue-400 hover:bg-blue-500/30"
                          onClick={(e) => {
                            e.stopPropagation();
                            focusOnDriver(driver);
                          }}
                        >
                          <Eye className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <Truck className="w-12 h-12 text-slate-600 mx-auto mb-2" />
                <p className="text-slate-400 text-sm">Aucun livreur en ligne</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="p-4 border-b border-slate-700">
          <h3 className="font-bold flex items-center gap-2">
            <Package className="w-5 h-5 text-green-400" />
            Commandes récentes
          </h3>
        </div>
        
        <div className="overflow-x-auto touch-scroll-x">
          <table className="w-full">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="text-left p-3 text-xs font-medium text-slate-400">Commande</th>
                <th className="text-left p-3 text-xs font-medium text-slate-400">Client</th>
                <th className="text-left p-3 text-xs font-medium text-slate-400">Livreur</th>
                <th className="text-left p-3 text-xs font-medium text-slate-400">Total</th>
                <th className="text-left p-3 text-xs font-medium text-slate-400">Statut</th>
                <th className="text-left p-3 text-xs font-medium text-slate-400">Date</th>
              </tr>
            </thead>
            <tbody>
              {orders.slice(0, 10).map((order) => (
                <tr key={order.id} className="border-t border-slate-700 hover:bg-slate-700/30">
                  <td className="p-3">
                    <p className="font-mono text-sm text-white">#{order.order_number?.slice(-8)}</p>
                  </td>
                  <td className="p-3">
                    <p className="text-sm text-white">{order.customer_name}</p>
                    <p className="text-xs text-slate-400">{order.delivery_address?.city}</p>
                  </td>
                  <td className="p-3">
                    {order.driver_name ? (
                      <span className="text-sm text-white">{order.driver_name}</span>
                    ) : (
                      <span className="text-xs text-amber-400">Non assigné</span>
                    )}
                  </td>
                  <td className="p-3">
                    <span className="font-medium text-white">{formatPrice(order.total_fcfa)}</span>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium bg-${ORDER_STATUSES[order.status]?.color}-500/20 text-${ORDER_STATUSES[order.status]?.color}-400`}>
                      {ORDER_STATUSES[order.status]?.label}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-slate-400">
                    {new Date(order.created_at).toLocaleString('fr-FR', { 
                      day: '2-digit', 
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {orders.length === 0 && (
          <div className="p-12 text-center">
            <Package className="w-12 h-12 text-slate-600 mx-auto mb-2" />
            <p className="text-slate-400">Aucune commande</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLiveTracking;
